"use client"
import { Formik } from "formik"
import * as Yup from "yup"
import { toast } from '../../common/Toast/toast';
import { useNavigate, useParams } from "react-router-dom"
import { useEffect, useRef, useState } from "react"

import { createClassroom, updateClassroom, getClassroomById } from "../../../../services/ClassroomService.js"
import { fetchAllBranches } from "../../../../services/BranchService.js"
import { useAuth } from "../../../../context/AuthContext"
import "../../Trainer/TrainerDashboard.css"
import "../../common/lms-pages.css"

// Rendered on its own route, and inside the classroom list as a dialog.
function AddClassroom({ embedded = false, recordId, onDone, onCancel }) {
  const navigate = useNavigate()
  const { id: routeId } = useParams()
  const id = recordId || routeId
  const [initialValues, setInitialValues] = useState({
    slug: "",
    classroomNo: "",
    description: "",
    branch: "",
    floor: "",
    capacity: "",
    startTime: "",
    endTime: "",
    date: "",
    status: "",
  })
  const [isLoading, setIsLoading] = useState(false)
  const [branchOptions, setBranchOptions] = useState([])
  const [branchesLoading, setBranchesLoading] = useState(false)
  const isEditMode = Boolean(id)

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        setBranchesLoading(true)
        const branches = await fetchAllBranches("")
        setBranchOptions(branches.map((b) => ({ label: b.name, value: b.id })))
      } catch (error) {
        toast.error("Failed to fetch branches")
      } finally {
        setBranchesLoading(false)
      }
    }
    fetchBranches()
  }, [])

  const hasShownToast = useRef(false);
  const { hasPermission } = useAuth();
  useEffect(() => {
    const requiredOp = isEditMode ? 'update' : 'add';
    if (!hasPermission('list-classroom', requiredOp, 'lms')) {
      if (!hasShownToast.current) {
        toast.error(
          isEditMode
            ? 'You do not have permission to edit classrooms.'
            : 'You do not have permission to add classrooms.'
        );
        navigate('/');
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate, isEditMode]);

  const validationSchema = Yup.object({
    slug: Yup.string().required("Slug is required"),
    classroomNo: Yup.string().required("Classroom number is required"),
    description: Yup.string().required("Description is required"),
    branch: Yup.string().required("Branch is required"),
    floor: Yup.string().required("Floor is required"),
    capacity: Yup.number()
      .typeError("Capacity must be a number")
      .integer("Capacity must be a whole number")
      .min(1, "Capacity must be at least 1")
      .required("Capacity is required"),
    startTime: Yup.string().required("Start time is required"),
    endTime: Yup.string().required("End time is required"),
    date: Yup.string().required("Date is required"),
    status: Yup.string().required("Status is required"),
  })

  useEffect(() => {
    if (isEditMode) {
      fetchClassroomData()
    }
  }, [id])

  const fetchClassroomData = async () => {
    try {
      setIsLoading(true)
      const response = await getClassroomById(id)
      const classroomData = response.data

      // datetime-local / date inputs read and write LOCAL wall-clock time, not
      // UTC — using toISOString() here mislabels UTC values as local, causing
      // start/end time to silently shift by the timezone offset on every edit.
      const pad = (n) => String(n).padStart(2, "0")

      const formatDateTimeLocal = (dateString) => {
        const date = new Date(dateString)
        if (Number.isNaN(date.getTime())) return ""
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
      }

      const formatDateLocal = (dateString) => {
        const date = new Date(dateString)
        if (Number.isNaN(date.getTime())) return ""
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
      }

      setInitialValues({
        slug: classroomData.slug || "",
        classroomNo: classroomData.classroomNo || "",
        description: classroomData.description || "",
        branch: classroomData.branch?.id || classroomData.branch || "",
        floor: classroomData.floor || "",
        capacity: classroomData.capacity ?? "",
        startTime: formatDateTimeLocal(classroomData.startTime),
        endTime: formatDateTimeLocal(classroomData.endTime),
        date: formatDateLocal(classroomData.date),
        status: classroomData.status || "",
      })
    } catch (error) {
    
      toast.error("Failed to fetch classroom data")
      navigate("/list-classroom")
    } finally {
      setIsLoading(false)
    }
  }

  // Function to handle slug input - only allow spaces and basic characters
  const handleSlugChange = (e, setFieldValue) => {
    const value = e.target.value;
    
    let formattedValue = value;
    
    // Replace spaces with hyphens in real-time
    formattedValue = formattedValue.replace(/\s+/g, '-');
    
    // Ensure it starts with "/"
    if (formattedValue && !formattedValue.startsWith("/")) {
      formattedValue = "/" + formattedValue;
    }
    
    // Only allow letters, numbers, hyphens, and forward slash
    formattedValue = formattedValue.replace(/[^a-zA-Z0-9/-]/g, '');
    
    setFieldValue('slug', formattedValue);
  }

  // Prevent special characters from being entered
  const handleSlugKeyPress = (e) => {
    const key = e.key;
    // Allow only letters, numbers, space, backspace, delete, arrow keys, and slash
    if (!/^[a-zA-Z0-9\s/]$/.test(key) && 
        key !== 'Backspace' && 
        key !== 'Delete' && 
        key !== 'ArrowLeft' && 
        key !== 'ArrowRight' &&
        key !== 'Tab') {
      e.preventDefault();
    }
  }

  if (isLoading) {
    return (
      <div className="att-picklist-foot">
        <span className="pg-spinner att-spinner" aria-hidden="true" />
        <span>Loading classroom...</span>
      </div>
    )
  }

  const leave = () => {
    if (embedded) onCancel?.()
    else navigate("/list-classroom")
  }

  return (
    <div className={embedded ? "att-embed" : "pg"}>
      {!embedded && (
        <header className="pg-head">
          <div className="pg-head-main">
            <h4 className="pg-title">
              {isEditMode ? "Edit classroom" : "Add classroom"}
            </h4>
          </div>
          <div className="pg-head-actions">
            <button type="button" className="pg-btn" onClick={leave}>
              Back to classrooms
            </button>
          </div>
        </header>
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize={true}
        onSubmit={async (values, { resetForm, setSubmitting }) => {
          try {
            setSubmitting(true)
            const classroomData = {
              ...values,
              capacity: Number(values.capacity),
              startTime: new Date(values.startTime).toISOString(),
              endTime: new Date(values.endTime).toISOString(),
              date: new Date(values.date).toISOString(),
            }
            if (isEditMode) {
              await updateClassroom(id, classroomData)
              toast.success("Classroom updated successfully!")
            } else {
              await createClassroom(classroomData)
              toast.success("Classroom created successfully!")
              resetForm()
            }
            if (embedded) {
              onDone?.()
              return
            }
            navigate("/list-classroom")
          } catch (error) {
            toast.error(`Failed to ${isEditMode ? 'update' : 'create'} classroom. Please try again.`)
          } finally {
            setSubmitting(false)
          }
        }}
      >
        {({ values, errors, touched, isSubmitting, setFieldValue, handleSubmit }) => {
          const err = (f) => (touched[f] ? errors[f] : "")
          const set = (f) => (e) => setFieldValue(f, e.target.value)
          const ready =
            String(values.classroomNo || "").trim() &&
            String(values.branch || "").trim() &&
            String(values.date || "").trim() &&
            String(values.startTime || "").trim() &&
            String(values.endTime || "").trim() &&
            !errors.classroomNo &&
            !errors.branch &&
            !errors.date &&
            !errors.startTime &&
            !errors.endTime &&
            !errors.capacity

          return (
            <form className="att-form" noValidate onSubmit={handleSubmit}>
              <div className="att-stage">
                <div className="att-stage-inner">
                  {/* One step. Ten fields, but they are all the same
                      question -- which room, where, and when -- so a
                      Continue button between them would buy nothing. */}
                  <section className="att-step">
                    <div className="mcq-options">
                      <div className="pg-field">
                        <label className="pg-label" htmlFor="ac-no">
                          Classroom
                        </label>
                        <input
                          id="ac-no"
                          type="text"
                          className="pg-input"
                          placeholder="e.g. 204"
                          value={values.classroomNo || ""}
                          onChange={set("classroomNo")}
                        />
                        {err("classroomNo") && (
                          <p className="att-error">{err("classroomNo")}</p>
                        )}
                      </div>

                      <div className="pg-field">
                        <label className="pg-label" htmlFor="ac-slug">
                          Slug
                          <span className="att-card-count">optional</span>
                        </label>
                        <input
                          id="ac-slug"
                          type="text"
                          className="pg-input"
                          placeholder="e.g. /seven-mentor"
                          value={values.slug || ""}
                          onChange={set("slug")}
                        />
                        {err("slug") && <p className="att-error">{err("slug")}</p>}
                      </div>
                    </div>

                    <div className="mcq-options">
                      <div className="pg-field">
                        <label className="pg-label" htmlFor="ac-branch">
                          Branch
                        </label>
                        <select
                          id="ac-branch"
                          className="pg-select"
                          value={values.branch || ""}
                          onChange={set("branch")}
                        >
                          <option value="">Select a branch</option>
                          {branchOptions.map((b) => (
                            <option key={b.value} value={b.value}>
                              {b.label}
                            </option>
                          ))}
                        </select>
                        {err("branch") && (
                          <p className="att-error">{err("branch")}</p>
                        )}
                      </div>

                      <div className="pg-field">
                        <label className="pg-label" htmlFor="ac-floor">
                          Floor
                        </label>
                        <input
                          id="ac-floor"
                          type="text"
                          className="pg-input"
                          placeholder="e.g. 2nd"
                          value={values.floor || ""}
                          onChange={set("floor")}
                        />
                        {err("floor") && <p className="att-error">{err("floor")}</p>}
                      </div>

                      <div className="pg-field">
                        <label className="pg-label" htmlFor="ac-capacity">
                          Capacity
                        </label>
                        <input
                          id="ac-capacity"
                          type="text"
                          inputMode="numeric"
                          className="pg-input"
                          placeholder="0"
                          value={values.capacity ?? ""}
                          onChange={(e) => {
                            const v = e.target.value
                            if (/^\d*$/.test(v)) setFieldValue("capacity", v)
                          }}
                        />
                        {err("capacity") && (
                          <p className="att-error">{err("capacity")}</p>
                        )}
                      </div>
                    </div>

                    <div className="mcq-options">
                      <div className="pg-field">
                        <label className="pg-label" htmlFor="ac-date">
                          Date
                        </label>
                        <input
                          id="ac-date"
                          type="date"
                          className="pg-input"
                          value={values.date || ""}
                          onChange={set("date")}
                        />
                        {err("date") && <p className="att-error">{err("date")}</p>}
                      </div>

                      <div className="pg-field">
                        <label className="pg-label" htmlFor="ac-start">
                          Starts
                        </label>
                        <input
                          id="ac-start"
                          type="datetime-local"
                          className="pg-input"
                          value={values.startTime || ""}
                          onChange={set("startTime")}
                        />
                        {err("startTime") && (
                          <p className="att-error">{err("startTime")}</p>
                        )}
                      </div>

                      <div className="pg-field">
                        <label className="pg-label" htmlFor="ac-end">
                          Ends
                        </label>
                        <input
                          id="ac-end"
                          type="datetime-local"
                          className="pg-input"
                          min={values.startTime || undefined}
                          value={values.endTime || ""}
                          onChange={set("endTime")}
                        />
                        {err("endTime") && (
                          <p className="att-error">{err("endTime")}</p>
                        )}
                      </div>

                      <div className="pg-field">
                        <label className="pg-label" htmlFor="ac-status">
                          Status
                        </label>
                        <select
                          id="ac-status"
                          className="pg-select"
                          value={values.status || ""}
                          onChange={set("status")}
                        >
                          <option value="">Select a status</option>
                          <option value="available">Available</option>
                          <option value="occupied">Occupied</option>
                          <option value="maintenance">Maintenance</option>
                        </select>
                        {err("status") && (
                          <p className="att-error">{err("status")}</p>
                        )}
                      </div>
                    </div>

                    <div className="pg-field">
                      <label className="pg-label" htmlFor="ac-desc">
                        Description
                        <span className="att-card-count">optional</span>
                      </label>
                      <textarea
                        id="ac-desc"
                        rows={3}
                        className="pg-input pg-textarea"
                        placeholder="Anything worth knowing about the room"
                        value={values.description || ""}
                        onChange={set("description")}
                      />
                      {err("description") && (
                        <p className="att-error">{err("description")}</p>
                      )}
                    </div>
                  </section>
                </div>
              </div>

              <div className="att-actions">
                <button type="button" className="pg-btn" onClick={leave}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="pg-btn pg-btn--main"
                  disabled={isSubmitting || !ready}
                >
                  {isSubmitting ? (
                    <>
                      <span className="pg-spinner att-spinner" aria-hidden="true" />
                      {isEditMode ? "Updating..." : "Creating..."}
                    </>
                  ) : isEditMode ? (
                    "Update classroom"
                  ) : (
                    "Create classroom"
                  )}
                </button>
              </div>
            </form>
          )
        }}
      </Formik>
    </div>
  )
}

export default AddClassroom
